import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Loader2, Search, MapPin, Users, Star, Wifi, Car, 
  Coffee, Home, Building2, Calendar, Filter, ExternalLink,
  ChevronLeft, Check, ChevronsUpDown, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Full ISO 3166-1 country list with codes
const COUNTRIES = [
  { code: "AF", name: { en: "Afghanistan", et: "Afganistan", fi: "Afganistan" } },
  { code: "AL", name: { en: "Albania", et: "Albaania", fi: "Albania" } },
  { code: "DZ", name: { en: "Algeria", et: "Alžeeria", fi: "Algeria" } },
  { code: "AD", name: { en: "Andorra", et: "Andorra", fi: "Andorra" } },
  { code: "AO", name: { en: "Angola", et: "Angola", fi: "Angola" } },
  { code: "AR", name: { en: "Argentina", et: "Argentina", fi: "Argentiina" } },
  { code: "AM", name: { en: "Armenia", et: "Armeenia", fi: "Armenia" } },
  { code: "AU", name: { en: "Australia", et: "Austraalia", fi: "Australia" } },
  { code: "AT", name: { en: "Austria", et: "Austria", fi: "Itävalta" } },
  { code: "AZ", name: { en: "Azerbaijan", et: "Aserbaidžaan", fi: "Azerbaidžan" } },
  { code: "BS", name: { en: "Bahamas", et: "Bahama", fi: "Bahama" } },
  { code: "BH", name: { en: "Bahrain", et: "Bahrein", fi: "Bahrain" } },
  { code: "BD", name: { en: "Bangladesh", et: "Bangladesh", fi: "Bangladesh" } },
  { code: "BY", name: { en: "Belarus", et: "Valgevene", fi: "Valko-Venäjä" } },
  { code: "BE", name: { en: "Belgium", et: "Belgia", fi: "Belgia" } },
  { code: "BZ", name: { en: "Belize", et: "Belize", fi: "Belize" } },
  { code: "BJ", name: { en: "Benin", et: "Benin", fi: "Benin" } },
  { code: "BT", name: { en: "Bhutan", et: "Bhutan", fi: "Bhutan" } },
  { code: "BO", name: { en: "Bolivia", et: "Boliivia", fi: "Bolivia" } },
  { code: "BA", name: { en: "Bosnia and Herzegovina", et: "Bosnia ja Hertsegoviina", fi: "Bosnia ja Hertsegovina" } },
  { code: "BW", name: { en: "Botswana", et: "Botswana", fi: "Botswana" } },
  { code: "BR", name: { en: "Brazil", et: "Brasiilia", fi: "Brasilia" } },
  { code: "BN", name: { en: "Brunei", et: "Brunei", fi: "Brunei" } },
  { code: "BG", name: { en: "Bulgaria", et: "Bulgaaria", fi: "Bulgaria" } },
  { code: "KH", name: { en: "Cambodia", et: "Kambodža", fi: "Kambodža" } },
  { code: "CM", name: { en: "Cameroon", et: "Kamerun", fi: "Kamerun" } },
  { code: "CA", name: { en: "Canada", et: "Kanada", fi: "Kanada" } },
  { code: "CL", name: { en: "Chile", et: "Tšiili", fi: "Chile" } },
  { code: "CN", name: { en: "China", et: "Hiina", fi: "Kiina" } },
  { code: "CO", name: { en: "Colombia", et: "Colombia", fi: "Kolumbia" } },
  { code: "CR", name: { en: "Costa Rica", et: "Costa Rica", fi: "Costa Rica" } },
  { code: "HR", name: { en: "Croatia", et: "Horvaatia", fi: "Kroatia" } },
  { code: "CU", name: { en: "Cuba", et: "Kuuba", fi: "Kuuba" } },
  { code: "CY", name: { en: "Cyprus", et: "Küpros", fi: "Kypros" } },
  { code: "CZ", name: { en: "Czech Republic", et: "Tšehhi", fi: "Tšekki" } },
  { code: "DK", name: { en: "Denmark", et: "Taani", fi: "Tanska" } },
  { code: "DO", name: { en: "Dominican Republic", et: "Dominikaani Vabariik", fi: "Dominikaaninen tasavalta" } },
  { code: "EC", name: { en: "Ecuador", et: "Ecuador", fi: "Ecuador" } },
  { code: "EG", name: { en: "Egypt", et: "Egiptus", fi: "Egypti" } },
  { code: "SV", name: { en: "El Salvador", et: "El Salvador", fi: "El Salvador" } },
  { code: "EE", name: { en: "Estonia", et: "Eesti", fi: "Viro" } },
  { code: "ET", name: { en: "Ethiopia", et: "Etioopia", fi: "Etiopia" } },
  { code: "FI", name: { en: "Finland", et: "Soome", fi: "Suomi" } },
  { code: "FR", name: { en: "France", et: "Prantsusmaa", fi: "Ranska" } },
  { code: "GE", name: { en: "Georgia", et: "Gruusia", fi: "Georgia" } },
  { code: "DE", name: { en: "Germany", et: "Saksamaa", fi: "Saksa" } },
  { code: "GH", name: { en: "Ghana", et: "Ghana", fi: "Ghana" } },
  { code: "GR", name: { en: "Greece", et: "Kreeka", fi: "Kreikka" } },
  { code: "GT", name: { en: "Guatemala", et: "Guatemala", fi: "Guatemala" } },
  { code: "HN", name: { en: "Honduras", et: "Honduras", fi: "Honduras" } },
  { code: "HK", name: { en: "Hong Kong", et: "Hongkong", fi: "Hongkong" } },
  { code: "HU", name: { en: "Hungary", et: "Ungari", fi: "Unkari" } },
  { code: "IS", name: { en: "Iceland", et: "Island", fi: "Islanti" } },
  { code: "IN", name: { en: "India", et: "India", fi: "Intia" } },
  { code: "ID", name: { en: "Indonesia", et: "Indoneesia", fi: "Indonesia" } },
  { code: "IR", name: { en: "Iran", et: "Iraan", fi: "Iran" } },
  { code: "IQ", name: { en: "Iraq", et: "Iraak", fi: "Irak" } },
  { code: "IE", name: { en: "Ireland", et: "Iirimaa", fi: "Irlanti" } },
  { code: "IL", name: { en: "Israel", et: "Iisrael", fi: "Israel" } },
  { code: "IT", name: { en: "Italy", et: "Itaalia", fi: "Italia" } },
  { code: "JM", name: { en: "Jamaica", et: "Jamaica", fi: "Jamaika" } },
  { code: "JP", name: { en: "Japan", et: "Jaapan", fi: "Japani" } },
  { code: "JO", name: { en: "Jordan", et: "Jordaania", fi: "Jordania" } },
  { code: "KZ", name: { en: "Kazakhstan", et: "Kasahstan", fi: "Kazakstan" } },
  { code: "KE", name: { en: "Kenya", et: "Kenya", fi: "Kenia" } },
  { code: "KR", name: { en: "South Korea", et: "Lõuna-Korea", fi: "Etelä-Korea" } },
  { code: "KW", name: { en: "Kuwait", et: "Kuveit", fi: "Kuwait" } },
  { code: "KG", name: { en: "Kyrgyzstan", et: "Kõrgõzstan", fi: "Kirgisia" } },
  { code: "LA", name: { en: "Laos", et: "Laos", fi: "Laos" } },
  { code: "LV", name: { en: "Latvia", et: "Läti", fi: "Latvia" } },
  { code: "LB", name: { en: "Lebanon", et: "Liibanon", fi: "Libanon" } },
  { code: "LY", name: { en: "Libya", et: "Liibüa", fi: "Libya" } },
  { code: "LI", name: { en: "Liechtenstein", et: "Liechtenstein", fi: "Liechtenstein" } },
  { code: "LT", name: { en: "Lithuania", et: "Leedu", fi: "Liettua" } },
  { code: "LU", name: { en: "Luxembourg", et: "Luksemburg", fi: "Luxemburg" } },
  { code: "MO", name: { en: "Macau", et: "Macau", fi: "Macao" } },
  { code: "MY", name: { en: "Malaysia", et: "Malaisia", fi: "Malesia" } },
  { code: "MV", name: { en: "Maldives", et: "Maldiivid", fi: "Malediivit" } },
  { code: "MT", name: { en: "Malta", et: "Malta", fi: "Malta" } },
  { code: "MX", name: { en: "Mexico", et: "Mehhiko", fi: "Meksiko" } },
  { code: "MD", name: { en: "Moldova", et: "Moldova", fi: "Moldova" } },
  { code: "MC", name: { en: "Monaco", et: "Monaco", fi: "Monaco" } },
  { code: "MN", name: { en: "Mongolia", et: "Mongoolia", fi: "Mongolia" } },
  { code: "ME", name: { en: "Montenegro", et: "Montenegro", fi: "Montenegro" } },
  { code: "MA", name: { en: "Morocco", et: "Maroko", fi: "Marokko" } },
  { code: "MZ", name: { en: "Mozambique", et: "Mosambiik", fi: "Mosambik" } },
  { code: "MM", name: { en: "Myanmar", et: "Myanmar", fi: "Myanmar" } },
  { code: "NA", name: { en: "Namibia", et: "Namiibia", fi: "Namibia" } },
  { code: "NP", name: { en: "Nepal", et: "Nepal", fi: "Nepal" } },
  { code: "NL", name: { en: "Netherlands", et: "Holland", fi: "Alankomaat" } },
  { code: "NZ", name: { en: "New Zealand", et: "Uus-Meremaa", fi: "Uusi-Seelanti" } },
  { code: "NI", name: { en: "Nicaragua", et: "Nicaragua", fi: "Nicaragua" } },
  { code: "NG", name: { en: "Nigeria", et: "Nigeeria", fi: "Nigeria" } },
  { code: "MK", name: { en: "North Macedonia", et: "Põhja-Makedoonia", fi: "Pohjois-Makedonia" } },
  { code: "NO", name: { en: "Norway", et: "Norra", fi: "Norja" } },
  { code: "OM", name: { en: "Oman", et: "Omaan", fi: "Oman" } },
  { code: "PK", name: { en: "Pakistan", et: "Pakistan", fi: "Pakistan" } },
  { code: "PA", name: { en: "Panama", et: "Panama", fi: "Panama" } },
  { code: "PY", name: { en: "Paraguay", et: "Paraguay", fi: "Paraguay" } },
  { code: "PE", name: { en: "Peru", et: "Peruu", fi: "Peru" } },
  { code: "PH", name: { en: "Philippines", et: "Filipiinid", fi: "Filippiinit" } },
  { code: "PL", name: { en: "Poland", et: "Poola", fi: "Puola" } },
  { code: "PT", name: { en: "Portugal", et: "Portugal", fi: "Portugali" } },
  { code: "QA", name: { en: "Qatar", et: "Katar", fi: "Qatar" } },
  { code: "RO", name: { en: "Romania", et: "Rumeenia", fi: "Romania" } },
  { code: "RU", name: { en: "Russia", et: "Venemaa", fi: "Venäjä" } },
  { code: "SA", name: { en: "Saudi Arabia", et: "Saudi Araabia", fi: "Saudi-Arabia" } },
  { code: "SN", name: { en: "Senegal", et: "Senegal", fi: "Senegal" } },
  { code: "RS", name: { en: "Serbia", et: "Serbia", fi: "Serbia" } },
  { code: "SG", name: { en: "Singapore", et: "Singapur", fi: "Singapore" } },
  { code: "SK", name: { en: "Slovakia", et: "Slovakkia", fi: "Slovakia" } },
  { code: "SI", name: { en: "Slovenia", et: "Sloveenia", fi: "Slovenia" } },
  { code: "ZA", name: { en: "South Africa", et: "Lõuna-Aafrika", fi: "Etelä-Afrikka" } },
  { code: "ES", name: { en: "Spain", et: "Hispaania", fi: "Espanja" } },
  { code: "LK", name: { en: "Sri Lanka", et: "Sri Lanka", fi: "Sri Lanka" } },
  { code: "SE", name: { en: "Sweden", et: "Rootsi", fi: "Ruotsi" } },
  { code: "CH", name: { en: "Switzerland", et: "Šveits", fi: "Sveitsi" } },
  { code: "TW", name: { en: "Taiwan", et: "Taiwan", fi: "Taiwan" } },
  { code: "TJ", name: { en: "Tajikistan", et: "Tadžikistan", fi: "Tadžikistan" } },
  { code: "TZ", name: { en: "Tanzania", et: "Tansaania", fi: "Tansania" } },
  { code: "TH", name: { en: "Thailand", et: "Tai", fi: "Thaimaa" } },
  { code: "TN", name: { en: "Tunisia", et: "Tuneesia", fi: "Tunisia" } },
  { code: "TR", name: { en: "Turkey", et: "Türgi", fi: "Turkki" } },
  { code: "TM", name: { en: "Turkmenistan", et: "Türkmenistan", fi: "Turkmenistan" } },
  { code: "UA", name: { en: "Ukraine", et: "Ukraina", fi: "Ukraina" } },
  { code: "AE", name: { en: "United Arab Emirates", et: "Araabia Ühendemiraadid", fi: "Arabiemiirikunnat" } },
  { code: "GB", name: { en: "United Kingdom", et: "Ühendkuningriik", fi: "Iso-Britannia" } },
  { code: "US", name: { en: "United States", et: "Ameerika Ühendriigid", fi: "Yhdysvallat" } },
  { code: "UY", name: { en: "Uruguay", et: "Uruguay", fi: "Uruguay" } },
  { code: "UZ", name: { en: "Uzbekistan", et: "Usbekistan", fi: "Uzbekistan" } },
  { code: "VE", name: { en: "Venezuela", et: "Venezuela", fi: "Venezuela" } },
  { code: "VN", name: { en: "Vietnam", et: "Vietnam", fi: "Vietnam" } },
  { code: "YE", name: { en: "Yemen", et: "Jeemen", fi: "Jemen" } },
  { code: "ZM", name: { en: "Zambia", et: "Sambia", fi: "Sambia" } },
  { code: "ZW", name: { en: "Zimbabwe", et: "Zimbabwe", fi: "Zimbabwe" } },
];

// Amenity icons mapping
const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  kitchen: Coffee,
  workspace: Home,
};

/**
 * Searchable Country Combobox
 */
const CountryCombobox = ({ value, onChange, language, t }) => {
  const [open, setOpen] = useState(false);
  
  const getCountryName = (country) => {
    return country.name[language] || country.name.en;
  };
  
  const selectedCountry = COUNTRIES.find(c => c.code === value);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          data-testid="accommodation-country-select"
        >
          <Globe className="h-4 w-4 mr-2 text-gray-400" />
          {value && value !== 'all' 
            ? getCountryName(selectedCountry) 
            : t('accommodation.browse.allCountries')
          }
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('accommodation.browse.searchCountry')} />
          <CommandList>
            <CommandEmpty>{t('accommodation.browse.noCountryFound')}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all"
                onSelect={() => {
                  onChange('all');
                  setOpen(false);
                }}
              >
                <Check className={cn("mr-2 h-4 w-4", value === 'all' ? "opacity-100" : "opacity-0")} />
                {t('accommodation.browse.allCountries')}
              </CommandItem>
              {COUNTRIES.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.code} ${getCountryName(country)}`}
                  onSelect={() => {
                    onChange(country.code);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === country.code ? "opacity-100" : "opacity-0")} />
                  {getCountryName(country)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * External Search Links Component (Airbnb / Booking.com fallback)
 */
const ExternalSearchLinks = ({ city, country, checkIn, checkOut, guests, t, showAlways = false }) => {
  const getAirbnbUrl = () => {
    const params = new URLSearchParams();
    if (city) params.append('query', city);
    if (checkIn) params.append('checkin', checkIn);
    if (checkOut) params.append('checkout', checkOut);
    if (guests) params.append('adults', guests);
    return `https://www.airbnb.com/s/${city || 'anywhere'}/homes?${params}`;
  };
  
  const getBookingUrl = () => {
    const params = new URLSearchParams();
    if (city) params.append('ss', city);
    if (checkIn) params.append('checkin', checkIn);
    if (checkOut) params.append('checkout', checkOut);
    if (guests) params.append('group_adults', guests);
    return `https://www.booking.com/searchresults.html?${params}`;
  };
  
  return (
    <Card className="border-dashed border-gray-300 bg-gray-50">
      <CardContent className="p-6 text-center">
        <h3 className="font-semibold text-gray-700 mb-2">
          {t('accommodation.external.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('accommodation.external.description')}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => window.open(getAirbnbUrl(), '_blank')}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            {t('accommodation.external.airbnb')}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => window.open(getBookingUrl(), '_blank')}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            {t('accommodation.external.booking')}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          {t('accommodation.external.note')}
        </p>
      </CardContent>
    </Card>
  );
};

/**
 * Accommodation Listing Card
 */
const ListingCard = ({ listing, t }) => {
  const mainPhoto = listing.photos?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400';
  
  return (
    <Link to={`/accommodation/${listing.listing_id}`} data-testid={`listing-card-${listing.listing_id}`}>
      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group" data-testid="accommodation-listing-card">
        <div className="relative h-48 overflow-hidden">
          <img 
            src={mainPhoto}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {listing.work_friendly && (
            <Badge className="absolute top-2 left-2 bg-blue-600" data-testid="work-friendly-badge">
              {t('accommodation.browse.workFriendly')}
            </Badge>
          )}
          {listing.booking_mode === 'instant' && (
            <Badge className="absolute top-2 right-2 bg-green-600" data-testid="instant-book-badge">
              {t('accommodation.browse.instantBook')}
            </Badge>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{listing.title}</h3>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="h-3 w-3" />
                {listing.city}, {listing.country}
              </p>
            </div>
            {listing.rating && (
              <div className="flex items-center gap-1 text-sm">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{listing.rating}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {listing.max_occupants}
            </span>
            {listing.bedrooms && (
              <span>{listing.bedrooms} {t('accommodation.listing.beds')}</span>
            )}
            <span className="capitalize">
              {t(`accommodation.types.${listing.accommodation_type}`) || listing.accommodation_type?.replace('_', ' ')}
            </span>
          </div>
          
          <div className="flex items-center gap-2 mt-3">
            {listing.amenities?.slice(0, 4).map((amenity) => {
              const Icon = AMENITY_ICONS[amenity] || Home;
              return (
                <span key={amenity} className="p-1.5 bg-gray-100 rounded" title={amenity}>
                  <Icon className="h-3 w-3 text-gray-500" />
                </span>
              );
            })}
          </div>
          
          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <div>
              <span className="text-lg font-bold text-gray-900">€{listing.nightly_price}</span>
              <span className="text-sm text-gray-500"> / {t('accommodation.booking.night')}</span>
              {listing.weekly_discount_percent > 0 && (
                <span className="text-xs text-green-600 block">
                  -{listing.weekly_discount_percent}% {t('accommodation.browse.weeklyDiscount')}
                </span>
              )}
            </div>
            <Button size="sm" variant="outline">
              {t('accommodation.browse.view')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

/**
 * Main Accommodation Page
 */
const AccommodationPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [country, setCountry] = useState(searchParams.get('country') || 'all');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [guests, setGuests] = useState(searchParams.get('guests') || '1');
  const [checkIn, setCheckIn] = useState(searchParams.get('check_in') || '');
  const [checkOut, setCheckOut] = useState(searchParams.get('check_out') || '');
  const [accommodationType, setAccommodationType] = useState(searchParams.get('type') || 'all');

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country && country !== 'all') params.append('country', country);
      if (city) params.append('city', city);
      if (guests) params.append('guests', guests);
      if (checkIn) params.append('check_in', checkIn);
      if (checkOut) params.append('check_out', checkOut);
      if (accommodationType && accommodationType !== 'all') params.append('accommodation_type', accommodationType);
      params.append('work_friendly', 'true');
      
      const response = await fetch(`${API_URL}/api/accommodation/listings?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch listings');
      }
      
      const data = await response.json();
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error(t('accommodation.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    
    // Update URL params for shareable links
    const params = new URLSearchParams();
    if (country && country !== 'all') params.set('country', country);
    if (city) params.set('city', city);
    if (guests && guests !== '1') params.set('guests', guests);
    if (checkIn) params.set('check_in', checkIn);
    if (checkOut) params.set('check_out', checkOut);
    if (accommodationType && accommodationType !== 'all') params.set('type', accommodationType);
    
    setSearchParams(params);
    fetchListings();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Navigation */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        {/* Top Navigation */}
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between border-b border-white/10">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <ChevronLeft className="h-4 w-4" />
            <span className="font-semibold">{t('common.keikkaHome')}</span>
          </Link>
          <div className="text-sm text-purple-200">
            {t('accommodation.tagline')}
          </div>
        </div>
        
        {/* Hero Section */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {t('accommodation.browse.title')}
          </h1>
          <p className="text-purple-100 text-lg">
            {t('accommodation.browse.subtitle')}
          </p>
        </div>
      </div>

      {/* Search Form */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="space-y-4" data-testid="accommodation-search-form">
              <div className="flex flex-wrap gap-3">
                {/* City Input */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder={t('accommodation.browse.searchPlaceholder')}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="pl-10"
                      data-testid="accommodation-city-input"
                    />
                  </div>
                </div>
                
                {/* Country Combobox (Searchable) */}
                <div className="w-48">
                  <CountryCombobox 
                    value={country} 
                    onChange={setCountry} 
                    language={language}
                    t={t}
                  />
                </div>
                
                {/* Dates */}
                <div className="w-36">
                  <Input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    placeholder={t('accommodation.browse.checkIn')}
                    data-testid="accommodation-checkin-input"
                  />
                </div>
                <div className="w-36">
                  <Input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    placeholder={t('accommodation.browse.checkOut')}
                    data-testid="accommodation-checkout-input"
                  />
                </div>
                
                {/* Guests */}
                <div className="w-28">
                  <Select value={guests} onValueChange={setGuests}>
                    <SelectTrigger data-testid="accommodation-guests-select">
                      <Users className="h-4 w-4 mr-2 text-gray-400" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n} {t('accommodation.browse.guest')}{n>1?'s':''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Search Button */}
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="accommodation-search-btn">
                  <Search className="h-4 w-4 mr-2" />
                  {t('accommodation.browse.search')}
                </Button>
              </div>
              
              {/* Type Filter Row */}
              <div className="flex items-center gap-3 pt-2 border-t">
                <Filter className="h-4 w-4 text-gray-400" />
                <Select value={accommodationType} onValueChange={setAccommodationType}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('accommodation.browse.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('accommodation.browse.allTypes')}</SelectItem>
                    <SelectItem value="entire_apartment">{t('accommodation.types.entire_apartment')}</SelectItem>
                    <SelectItem value="private_room">{t('accommodation.types.private_room')}</SelectItem>
                    <SelectItem value="shared_room">{t('accommodation.types.shared_room')}</SelectItem>
                    <SelectItem value="company_housing">{t('accommodation.types.company_housing')}</SelectItem>
                    <SelectItem value="worker_dorm">{t('accommodation.types.worker_dorm')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Results Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <>
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {total} {total === 1 ? t('accommodation.browse.foundSingle') : t('accommodation.browse.found')}
              </h2>
            </div>
            
            {/* Listings Grid */}
            {listings.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} t={t} />
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">{t('accommodation.browse.noListings')}</h3>
                  <p className="text-gray-500 mt-1">{t('accommodation.browse.noListingsDesc')}</p>
                </CardContent>
              </Card>
            )}
            
            {/* External Search Links (Fallback) */}
            <div className="mt-8">
              <ExternalSearchLinks 
                city={city}
                country={country}
                checkIn={checkIn}
                checkOut={checkOut}
                guests={guests}
                t={t}
                showAlways={listings.length === 0}
              />
            </div>
            
            {/* Host CTA */}
            <Card className="mt-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-100">
              <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('accommodation.cta.title')}</h3>
                  <p className="text-gray-600 mt-1">{t('accommodation.cta.subtitle')}</p>
                  <p className="text-sm text-purple-600 mt-2">{t('accommodation.cta.trial')}</p>
                </div>
                <Button asChild className="bg-purple-600 hover:bg-purple-700">
                  <Link to="/host/dashboard">
                    {t('accommodation.cta.button')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t bg-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between text-sm text-gray-500">
          <Link to="/" className="hover:text-purple-600 transition-colors">
            ← {t('common.backToMain')}
          </Link>
          <span>KEIKKA © 2026</span>
        </div>
      </div>
    </div>
  );
};

export default AccommodationPage;
